import { createElement } from "react";
import { toast } from "@/hooks/use-toast";
import { ErrorToastContent } from "@/components/ErrorToast";
import { getErrorMessage } from "@/utils/errorMessages";
import { supabase } from "@/integrations/supabase/client";

/**
 * Build a context object describing where the error happened.
 */
function buildContext(extra?: Record<string, unknown>): Record<string, unknown> {
  let addinMode = false;
  try {
    // @ts-ignore - Office is injected at runtime in Add-In
    addinMode = typeof Office !== "undefined" && !!Office.context;
  } catch {
    addinMode = false;
  }
  return {
    route: typeof window !== "undefined" ? window.location.pathname : undefined,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    addinMode,
    ...(extra || {}),
  };
}

/**
 * Cheap, non-cryptographic hash for de-dupe key.
 */
function simpleHash(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

export type AutoCaptureResult = {
  invoked: boolean;
  auto_refunded?: boolean;
  amount?: number;
  error?: string;
} | null;

/**
 * Send a fire-and-forget auto-capture report to admin so we see system errors
 * even when the user does NOT click "Report to support".
 *
 * - De-dupes the same (title|message) inside 5 minutes via sessionStorage,
 *   but only AFTER a successful invoke (so killed/aborted requests can retry).
 * - Server-side throttles per user (20/h).
 * - Logs failures to console for diagnosis (does not throw).
 */
async function autoCapture(
  title: string,
  message: string,
  technicalDetails: string,
  context: Record<string, unknown>
): Promise<AutoCaptureResult> {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;

    // Only send when authenticated — the function requires a JWT.
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      console.info("[autoCapture] skipped: no session");
      return null;
    }

    const dedupeKey = `auto-error-${simpleHash(`${title}|${message.slice(0, 200)}`)}`;
    const last = window.sessionStorage.getItem(dedupeKey);
    const now = Date.now();
    if (last && now - Number(last) < 5 * 60 * 1000) {
      console.info("[autoCapture] skipped: deduped");
      return null;
    }

    const { data, error } = await supabase.functions.invoke("auto-report-error", {
      body: {
        error_title: title,
        error_message: technicalDetails || message,
        error_context: {
          user_message: message,
          ...context,
          timestamp: new Date().toISOString(),
        },
      },
    });

    if (error) {
      console.error("[autoCapture] invoke failed", error);
      return { invoked: false, error: String(error?.message || error) };
    }

    // Set dedupe key only AFTER a successful invoke
    window.sessionStorage.setItem(dedupeKey, String(now));
    console.info("[autoCapture] response", data);
    return {
      invoked: true,
      auto_refunded: !!data?.auto_refunded,
      amount: data?.amount,
    };
  } catch (err) {
    console.error("[autoCapture] unexpected error", err);
    return { invoked: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * Show a destructive error toast with an auto-report explanation.
 * Use this for real server / system errors — NOT for form validation.
 *
 * Side-effect: the error is auto-captured to the admin error log
 * (de-duped, throttled) so admins see issues automatically, and the
 * server triggers an automatic credit refund where applicable.
 *
 * Returns the toast handle plus an `autoCapturePromise` so callers that need
 * to ensure the report was sent before tearing down the session (e.g. logout
 * + reload) can `await Promise.race([autoCapturePromise, timeout])`.
 */
export function showReportableError(
  rawError: unknown,
  options?: {
    contextLabel?: string;
    extraContext?: Record<string, unknown>;
    titleOverride?: string;
  }
) {
  const friendly = getErrorMessage(rawError, options?.contextLabel);
  const technicalDetails =
    rawError instanceof Error
      ? `${rawError.name}: ${rawError.message}${rawError.stack ? `\n\n${rawError.stack}` : ""}`
      : typeof rawError === "string"
      ? rawError
      : (() => {
          try {
            return JSON.stringify(rawError, null, 2);
          } catch {
            return String(rawError);
          }
        })();

  const context = buildContext(options?.extraContext);
  const title = options?.titleOverride || friendly.title;

  // Fire auto-capture and expose the promise so callers can await it
  const autoCapturePromise = autoCapture(title, friendly.message, technicalDetails, context);

  const t = toast({
    title,
    variant: "destructive",
    duration: 12000,
    description: createElement(ErrorToastContent, {
      title,
      message: friendly.message,
      technicalDetails,
      context,
      onClose: () => t.dismiss(),
    }),
  });

  return { ...t, autoCapturePromise };
}
